export const UNASSIGNED_WORK_PACKAGE = "Niet toegewezen";

function text(value) {
  return typeof value === "string" ? value.trim() : "";
}

function finiteMinutes(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function compareDutch(left, right) {
  return left.localeCompare(right, "nl-NL", { sensitivity: "base" });
}

function assignmentIndexes(report) {
  const byId = new Map();
  const byDescription = new Map();

  for (const assignment of report.work_assignments || []) {
    const id = text(assignment.work_assignment_id);
    const description = text(assignment.description).toLocaleLowerCase("nl-NL");
    if (id) byId.set(id, assignment);
    if (description) byDescription.set(description, assignment);
  }

  return { byId, byDescription };
}

function assignmentForEntry(entry, indexes) {
  const assignmentId = text(entry.work_assignment_id);
  if (assignmentId && indexes.byId.has(assignmentId)) return indexes.byId.get(assignmentId);

  const description = text(entry.work_description).toLocaleLowerCase("nl-NL");
  return description ? indexes.byDescription.get(description) : undefined;
}

function workPackageFor(entry, indexes) {
  const direct = text(entry.work_package);
  if (direct) return direct;
  const assignment = assignmentForEntry(entry, indexes);
  return text(assignment?.work_package) || UNASSIGNED_WORK_PACKAGE;
}

function legacyEquipmentUsage(usageDescription) {
  const usage = text(usageDescription);
  const match = usage.match(
    /^Machinist:\s*(.+?),\s*draaiuren:\s*([0-9]+(?:[.,][0-9]+)?)\s*$/i,
  );
  if (!match) return null;

  const hours = Number(match[2].replace(",", "."));
  if (!Number.isFinite(hours) || hours < 0) return null;
  return { machinistName: match[1].trim(), minutes: hours * 60 };
}

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort(compareDutch);
}

function assignmentMemberNames(report) {
  const namesByAssignment = new Map();
  const explicitLinks = Array.isArray(report.work_assignment_members)
    ? report.work_assignment_members
    : [];

  for (const link of explicitLinks) {
    const assignmentId = text(link.work_assignment_id);
    const employeeName = text(link.employee_name);
    if (!assignmentId || !employeeName) continue;
    const names = namesByAssignment.get(assignmentId) || [];
    if (!names.includes(employeeName)) names.push(employeeName);
    namesByAssignment.set(assignmentId, names);
  }

  if (explicitLinks.length === 0) {
    for (const entry of report.time_entries || []) {
      const assignmentId = text(entry.work_assignment_id);
      const employeeName = text(entry.employee_name);
      if (!assignmentId || !employeeName) continue;
      const names = namesByAssignment.get(assignmentId) || [];
      if (!names.includes(employeeName)) names.push(employeeName);
      namesByAssignment.set(assignmentId, names);
    }
  }

  for (const [assignmentId, names] of namesByAssignment.entries()) {
    namesByAssignment.set(assignmentId, uniqueSorted(names));
  }
  return namesByAssignment;
}

function sentenceFragment(value) {
  const cleaned = text(value);
  if (cleaned.length < 2) return cleaned.toLocaleLowerCase("nl-NL");
  const first = cleaned[0];
  const second = cleaned[1];
  if (
    first === first.toLocaleUpperCase("nl-NL") &&
    second === second.toLocaleLowerCase("nl-NL")
  ) {
    return first.toLocaleLowerCase("nl-NL") + cleaned.slice(1);
  }
  return cleaned;
}

function naturalList(values) {
  if (values.length === 0) return "";
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} en ${values[1]}`;
  return `${values.slice(0, -1).join(", ")} en ${values.at(-1)}`;
}

export function formatReportHours(minutes) {
  if (minutes === null) return "-";
  return (minutes / 60).toLocaleString("nl-NL", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
}

export function aggregateHoursByWorkPackage(report) {
  const indexes = assignmentIndexes(report);
  const totals = new Map();

  for (const entry of report.time_entries || []) {
    const minutes = finiteMinutes(entry.minutes);
    if (minutes === null) continue;

    const workPackage = workPackageFor(entry, indexes);
    const employeeName = text(entry.employee_name) || "Onbekende medewerker";
    const employeeKey = text(entry.report_team_member_id) ||
      employeeName.toLocaleLowerCase("nl-NL");
    const key = `${workPackage}\u0000${employeeKey}`;
    const existing = totals.get(key);

    if (existing) existing.minutes += minutes;
    else totals.set(key, { workPackage, employeeName, minutes });
  }

  return [...totals.values()].sort((left, right) =>
    compareDutch(left.workPackage, right.workPackage) ||
    compareDutch(left.employeeName, right.employeeName)
  );
}

export function aggregateEquipmentByWorkPackage(report) {
  const indexes = assignmentIndexes(report);
  const totals = new Map();

  for (const entry of report.equipment || []) {
    const workPackage = workPackageFor(entry, indexes);
    const equipmentName = text(entry.equipment_name) || "Onbekend materieel";
    const legacy = legacyEquipmentUsage(entry.usage_description);
    const machinistName = text(entry.machinist_name) || legacy?.machinistName || "-";
    const minutes = finiteMinutes(entry.minutes) ?? legacy?.minutes ?? null;
    const key = `${workPackage}\u0000${equipmentName}\u0000${machinistName}`;
    const existing = totals.get(key);

    if (existing) {
      if (minutes !== null) existing.minutes = (existing.minutes ?? 0) + minutes;
    } else {
      totals.set(key, { workPackage, equipmentName, machinistName, minutes });
    }
  }

  return [...totals.values()].sort((left, right) =>
    compareDutch(left.workPackage, right.workPackage) ||
    compareDutch(left.equipmentName, right.equipmentName) ||
    compareDutch(left.machinistName, right.machinistName)
  );
}

export function workPackageSummary(workPackage, descriptions) {
  const cleaned = uniqueSorted((descriptions || []).map(text));
  if (cleaned.length === 0) {
    return `Voor werkpakket ${workPackage} is nog geen werkbeschrijving vastgelegd.`;
  }
  return `De werkzaamheden binnen ${workPackage} bestonden uit ${
    naturalList(cleaned.map(sentenceFragment))
  }.`;
}

export function buildWorkPackageView(report) {
  const hourRows = aggregateHoursByWorkPackage(report);
  const equipmentRows = aggregateEquipmentByWorkPackage(report);
  const namesByAssignment = assignmentMemberNames(report);
  const packageNames = new Set();

  for (const assignment of report.work_assignments || []) {
    packageNames.add(text(assignment.work_package) || UNASSIGNED_WORK_PACKAGE);
  }
  for (const row of hourRows) packageNames.add(row.workPackage);
  for (const row of equipmentRows) packageNames.add(row.workPackage);

  return [...packageNames].sort(compareDutch).map((workPackage) => {
    const assignments = (report.work_assignments || [])
      .filter((assignment) =>
        (text(assignment.work_package) || UNASSIGNED_WORK_PACKAGE) === workPackage
      )
      .map((assignment) => ({
        ...assignment,
        employeeNames: namesByAssignment.get(text(assignment.work_assignment_id)) || [],
      }));
    const descriptions = assignments.map((assignment) => text(assignment.description)).filter(
      Boolean,
    );
    const reviewLines = [];
    for (const assignment of assignments) {
      const description = text(assignment.description);
      if (!description) continue;
      if (assignment.employeeNames.length === 0) {
        reviewLines.push(`Geen medewerker gekoppeld: ${description}`);
      } else {
        for (const employeeName of assignment.employeeNames) {
          reviewLines.push(`${employeeName}: ${description}`);
        }
      }
    }

    return {
      workPackage,
      assignments,
      hours: hourRows.filter((row) => row.workPackage === workPackage),
      equipment: equipmentRows.filter((row) => row.workPackage === workPackage),
      summary: workPackageSummary(workPackage, descriptions),
      reviewLines,
    };
  });
}
